import { CloseOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Checkbox, message, Popconfirm, Tooltip } from 'antd';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { randomId } from '../../reducers/util';
import { addIndexPeriod, deleteIndexPeriod, setActivePeriod, updateIndexPeriod } from './actions';
import { emptyPeriod } from './reducers';
import { Period, ReportConfig } from './types/CohortReport';

interface Props {
    config: ReportConfig
    activePeriod: Period
    addIndexPeriod(period: Period): any
    updateIndexPeriod(period: Period): any
    deleteIndexPeriod(period: Period): any
    setActivePeriod(period: Period): any
}

const ALL_VALUES: Period[] = [
    { id: '0', value: '<', label: 'Before Index', pre: true, at: false, post: false },
    { id: '0', value: '=', label: 'At Index', pre: false, at: true, post: false },
    { id: '0', value: '>', label: 'After Index', pre: false, at: false, post: true },
    { id: '0', value: '<=', label: 'On or Before Index', pre: true, at: true, post: false },
    { id: '0', value: '>=', label: 'On or After Index', pre: false, at: true, post: true },
    { id: '0', value: '<>', label: 'Not on Index', pre: true, at: false, post: true },
    { id: '0', value: 'all', label: 'Period', pre: true, at: true, post: true },
]

const IndexPeriodChooser = ({ config, activePeriod, addIndexPeriod, updateIndexPeriod, deleteIndexPeriod, setActivePeriod }: Props) => {
    const ButtonGroup = Button.Group;

    const [preIndexCheck, setPreIndexCheck] = useState(false)
    const [atIndexCheck, setAtIndexCheck] = useState(false)
    const [postIndexCheck, setPostIndexCheck] = useState(false)

    useEffect(() => {

        const fromLabelAndValue = ({ value }: Period): Period | undefined => {
            return _.find(ALL_VALUES, { 'value': value })
        }

        const result = fromLabelAndValue(activePeriod)
        if (!_.isNil(result)) {
            setPreIndexCheck(result.pre)
            setAtIndexCheck(result.at)
            setPostIndexCheck(result.post)
        }
    }, [activePeriod])

    const onCreateOrUpdate = () => {
        const result = toLabelAndValue(preIndexCheck, atIndexCheck, postIndexCheck)
        if (!_.isNil(result)) {
            const has = _.some(config.periods, ['value', result.value])
            if (!has) {
                if (_.isNil(activePeriod.id)) {
                    result.id = randomId()
                    addIndexPeriod(result)
                } else {
                    result.id = activePeriod.id
                    updateIndexPeriod(result)
                }
                updateConfigAndReset()
            } else {
                message.warning('This period has already been added.');
            }
        }
    }

    const onDeleteIndexPeriod = () => {
        deleteIndexPeriod(activePeriod)
        updateConfigAndReset()
    }

    const toLabelAndValue = (pre: boolean, at: boolean, post: boolean): Period | undefined => {
        return _.find(ALL_VALUES, { 'pre': pre, 'at': at, 'post': post })
    }

    const canAdd = () => {
        return !(preIndexCheck || atIndexCheck || postIndexCheck)
    }

    const hasNotBeenSaved = _.isNil(activePeriod.id)

    const canReset = () => {
        return !(preIndexCheck || atIndexCheck || postIndexCheck || !hasNotBeenSaved)
    }

    const updateConfigAndReset = () => {
        setActivePeriod(emptyPeriod())
        setPreIndexCheck(false)
        setAtIndexCheck(false)
        setPostIndexCheck(false)
    }

    return (
        <div className="period">
            Period<br />
            <Checkbox className="index-period" checked={preIndexCheck} onChange={e => setPreIndexCheck(e.target.checked)}>Pre Index</Checkbox><br />
            <Checkbox className="index-period" checked={atIndexCheck} onChange={e => setAtIndexCheck(e.target.checked)}>At Index</Checkbox><br />
            <Checkbox className="index-period" checked={postIndexCheck} onChange={e => setPostIndexCheck(e.target.checked)}>Post Index</Checkbox><br />
            <ButtonGroup>
                <Tooltip placement="bottom" title={hasNotBeenSaved ? "add" : "amend"}>
                    <Button shape="circle" icon={hasNotBeenSaved ? <PlusOutlined /> : <EditOutlined />} size="small" type="primary" disabled={canAdd()} onClick={onCreateOrUpdate} />
                </Tooltip>
                <Popconfirm
                    disabled={hasNotBeenSaved}
                    title="Are you sure you want to delete this Group?"
                    onConfirm={() => { onDeleteIndexPeriod() }}
                    okText="Yes"
                    cancelText="No">
                    <Tooltip placement="bottom" title="delete">
                        <Button shape="circle" icon={<DeleteOutlined />} size="small" type="primary" danger disabled={hasNotBeenSaved} />
                    </Tooltip>
                </Popconfirm>
                <Tooltip placement="bottom" title="cancel">
                    <Button shape="circle" icon={<CloseOutlined />} size="small" type="primary" className="ant-btn-warning" disabled={canReset()} onClick={updateConfigAndReset} />
                </Tooltip>
            </ButtonGroup>
        </div >
    );

}

const mapStateToProps = (state: any, ownProps: any) => {
    return {
        config: state.hru.report.reportConfig,
        activePeriod: state.hru.active.period
    }
}

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
    addIndexPeriod: (period: Period) => dispatch(addIndexPeriod(period)),
    updateIndexPeriod: (period: Period) => dispatch(updateIndexPeriod(period)),
    deleteIndexPeriod: (period: Period) => dispatch(deleteIndexPeriod(period)),
    setActivePeriod: (period: Period) => dispatch(setActivePeriod(period))
})

export default connect(mapStateToProps, mapDispatchToProps)(IndexPeriodChooser)